import { MeasureType } from '@/common/constants';
import { ContractStatusesEnum } from '@/common/constants/contract.statuses';
import { Button } from '@/components/ui/button';
import usePartyGrainStore from '@/features/parties-grain/store/usePartyGrainStore';
import useStore from '@/lib/store';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EditContract } from '../edit-contract';
import { generateGrainPDF } from './generateGrainFile';

interface IProps {
  load: any;
}

export const GeneralGrainData: FC<IProps> = ({ load }) => {
  const { t } = useTranslation();
  const {
    cropTypesByPlant,
    cropGenerationsByPlantId,
    fetchCropTypesByPlant,
    fetchCropGenerationsByPlantId,
  } = usePartyGrainStore();
  const { user } = useStore();

  useEffect(() => {
    if (load?.plant_id) {
      fetchCropTypesByPlant(load?.plant_id);
      fetchCropGenerationsByPlantId(load?.plant_id);
    }
  }, [load?.plant_id]);

  const isDeleted =
    load?.farmer_contract_status === ContractStatusesEnum.DELETED ||
    load?.farmer_contract_status === ContractStatusesEnum.CANCELED;

  const selectedType = useMemo(
    () =>
      cropTypesByPlant?.find((type) => type?.value == load?.crop_type_id)
        ?.label,
    [cropTypesByPlant, load],
  );

  const selectedGeneration = useMemo(
    () =>
      cropGenerationsByPlantId?.find(
        (type) => type?.value == load?.crop_generation_id,
      )?.label,
    [cropGenerationsByPlantId, load],
  );

  const list = useMemo(() => {
    return [
      {
        title: t('stir.receiving'),
        value: load?.buyer_tin || '---',
        span: 'col-span-1',
      },
      {
        title: t('name.product.receiving'),
        value: load?.cluster_name || '---',
        span: 'col-span-2',
      },
      {
        title: t('stir.delivery'),
        value: load?.farmer_tax_number || '---',
        span: 'col-span-1',
      },
      {
        title: t('name.product.delivery'),
        value: load?.farmer_name || '---',
        span: 'col-span-2',
      },
      {
        title: t('selection.type'),
        value: selectedType || '---',
        span: 'col-span-1',
      },
      {
        title: t('generation'),
        value: selectedGeneration || '---',
        span: 'col-span-1',
      },
      {
        title: t('cultivated.area'),
        value: load?.area ? `${load?.area} ${t('he')}` : '---',
        span: 'col-span-1',
      },
    ];
  }, [load, selectedType, selectedGeneration]);

  return (
    <div className="grid grid-cols-6 gap-6 w-full">
      {list?.map((item) => (
        <div key={item.title} className={item.span}>
          <p className="text-sm text-gray-600 mb-1">{item.title}:</p>
          <p className="text-base font-medium">{item.value}</p>
        </div>
      ))}
      <div className="col-span-1">
        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <p className="text-sm text-gray-600 mb-1">
              {t('contract.number')}:
            </p>
            <div className="flex items-center gap-2">
              <p className="text-base font-medium">
                {load?.farmer_contract_number || '---'}
              </p>
              {(load?.current_status === MeasureType.WEIGH_IN ||
                load?.current_status === MeasureType.WEIGH_OUT ||
                load?.current_status === MeasureType.ANALYSIS) && (
                <EditContract />
              )}
            </div>
          </div>
        </div>
      </div>
      <Button
        variant="outline"
        disabled={isDeleted}
        onClick={() =>
          generateGrainPDF(
            load!,
            user!,
            load?.number,
            load?.created_at,
            load?.farmer_tax_number,
          )
        }
        className="col-span-2 max-w-38"
      >
        {t('download.3dqim')}
      </Button>
    </div>
  );
};
